import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, X, Plus, HeartHandshake, Clock, Users } from 'lucide-react';
import Modal from '../components/Modal.tsx';
import PaybillSelect from '../components/PaybillSelect';
import MemberWelfareRecord from '../components/MemberWelfareRecord';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { usePermissions } from '../hooks/usePermissions';
import { useWorkflowSettings } from '../hooks/useWorkflowSettings.ts';
import {
  useAssistanceRequests, useNeedyList, useCreateAssistanceRequest, useVetRequest, useCreateWelfarePlanFromRequest,
} from '../hooks/useWelfareAssistance.ts';

const CATEGORIES = [
  ['medical', 'Medical'], ['bereavement', 'Bereavement'], ['emergency', 'Emergency'], ['education', 'Education'], ['other', 'Other'],
];
const STATUS_STYLE = {
  pending_vetting: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  completed: 'bg-gray-100 text-gray-700',
};
const STATUS_LABEL = { pending_vetting: 'Waiting for approval', approved: 'Approved', rejected: 'Rejected', completed: 'Completed' };
const money = (v) => 'KES ' + Number(v || 0).toLocaleString();

export default function WelfareAssistancePage() {
  const [tab, setTab] = useState('needy');
  const [showNew, setShowNew] = useState(false);
  const [vetting, setVetting] = useState(null); // { request, action }
  const [notes, setNotes] = useState('');
  const [planFor, setPlanFor] = useState(null); // request a collection is being started for
  const [planBill, setPlanBill] = useState('');
  const [openRecord, setOpenRecord] = useState(null); // request whose member record is shown
  const [form, setForm] = useState({ member_id: '', title: '', category: 'medical', description: '', amount_requested: '' });

  const { data: profile } = useGetProfile();
  const { can, canAny } = usePermissions();
  const { data: workflow } = useWorkflowSettings();
  const me = profile?.data?.member;
  const myUserId = profile?.data?.id ?? profile?.data?.user?.id;
  const isOfficial = !!profile?.data?.is_group_owner
    || (profile?.data?.current_role === 'admin' && canAny(['edit_welfare_plans', 'edit_welfare_plan', 'create_welfare_plans', 'create_welfare_plan']));
  const canCreatePlan = !!profile?.data?.is_group_owner || (isOfficial && canAny(['create_welfare_plans', 'create_welfare_plan']));

  const { data: membersData } = useMembers();
  const members = membersData?.data || [];
  const needy = useNeedyList();
  const waiting = useAssistanceRequests('pending_vetting');
  const rejected = useAssistanceRequests('rejected');
  const create = useCreateAssistanceRequest();
  const vet = useVetRequest();
  const createPlan = useCreateWelfarePlanFromRequest();

  const lists = { needy: needy.data || [], waiting: waiting.data || [], rejected: rejected.data || [] };
  const current = lists[tab];
  const loading = { needy: needy, waiting, rejected }[tab].isLoading;

  const submitNew = (e) => {
    e.preventDefault();
    const member_id = isOfficial ? Number(form.member_id) : me?.id;
    if (!member_id || !form.title.trim() || !(Number(form.amount_requested) >= 0)) return;
    create.mutate({ ...form, member_id, amount_requested: Number(form.amount_requested) }, {
      onSuccess: () => { setShowNew(false); setForm({ member_id: '', title: '', category: 'medical', description: '', amount_requested: '' }); },
    });
  };

  const submitVet = () => {
    vet.mutate({ id: vetting.request.id, action: vetting.action, notes: notes.trim() || undefined }, {
      onSuccess: () => { setVetting(null); setNotes(''); },
    });
  };

  const tabs = useMemo(() => [
    ['needy', 'Needy list', lists.needy.length, HeartHandshake],
    ['waiting', 'Waiting for approval', lists.waiting.length, Clock],
    ['rejected', 'Rejected', lists.rejected.length, X],
  ], [lists.needy.length, lists.waiting.length, lists.rejected.length]);

  return (
    <div className="bg-white rounded-xl shadow p-4 sm:p-6" data-testid="welfare-assistance">
      <Link to="/welfare" className="inline-flex items-center text-sm text-green-700 hover:underline mb-3"><ArrowLeft size={16} className="mr-1" /> Welfare plans</Link>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Welfare requests</h1>
        <button onClick={() => setShowNew(true)} className="flex items-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
          <Plus size={18} className="mr-2" /> {isOfficial ? 'New request' : 'Ask for help'}
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-5">
        {workflow?.welfare_vetting_enabled
          ? `A member who has not paid welfare contributions for 3 or more people needs ${workflow.welfare_vetting_approvals} approval${workflow.welfare_vetting_approvals > 1 ? 's' : ''} from officials. Everyone else joins the needy list at once.`
          : 'Requests join the needy list at once. Admins can switch on official approval in Settings > Features.'}
      </p>

      <div className="flex gap-2 overflow-x-auto mb-4" role="tablist">
        {tabs.map(([key, label, count, Icon]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`flex items-center whitespace-nowrap px-3 py-2 rounded-md text-sm ${tab === key ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
            <Icon size={16} className="mr-2" /> {label} <span className="ml-2 text-xs bg-gray-100 rounded-full px-2 py-0.5">{count}</span>
          </button>
        ))}
      </div>

      {loading ? <p className="text-gray-500 text-sm">Loading...</p> : current.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center">Nothing here yet.</p>
      ) : (
        <ul className="space-y-3">
          {current.map((r) => {
            const approvals = (r.vettings || []).filter((v) => v.action === 'approve').length;
            const mine = (r.vettings || []).find((v) => v.user_id === myUserId);
            const isMe = r.member?.user_id && r.member.user_id === myUserId;
            return (
              <li key={r.id} className="border border-gray-100 rounded-lg p-4">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-900">{r.member?.name || 'Member'} <span className="text-gray-400 font-normal">· {r.title}</span></p>
                    <p className="text-sm text-gray-600">{money(r.amount_requested)} · {r.category}</p>
                  </div>
                  <span className={`self-start text-xs px-2 py-1 rounded-full ${STATUS_STYLE[r.status] || 'bg-gray-100'}`}>{STATUS_LABEL[r.status] || r.status}</span>
                </div>
                {r.description && <p className="mt-2 text-sm text-gray-700">{r.description}</p>}
                {r.status === 'pending_vetting' && r.is_flagged && r.flag_reason && <p className="mt-2 text-sm text-amber-800 bg-amber-50 rounded p-2">{r.flag_reason}</p>}
                {r.status === 'pending_vetting' && (
                  <p className="mt-2 text-sm text-gray-600"><Users size={14} className="inline mr-1" /> {approvals} of {r.required_vettings_count} approvals</p>
                )}
                {(r.vettings || []).length > 0 && (
                  <ul className="mt-2 text-xs text-gray-600 space-y-1">
                    {r.vettings.map((v) => <li key={v.id}>{v.official?.name || 'Official'}: {v.action === 'approve' ? 'approved' : 'rejected'}{v.notes ? ` — ${v.notes}` : ''}</li>)}
                  </ul>
                )}
                {isOfficial && (
                  <button type="button" onClick={() => setOpenRecord(openRecord === r.id ? null : r.id)} className="mt-2 text-sm text-blue-700 hover:underline">
                    {openRecord === r.id ? 'Hide member record' : 'Check member record'}
                  </button>
                )}
                {isOfficial && openRecord === r.id && <div className="mt-2"><MemberWelfareRecord memberId={r.member_id} requestId={r.id} /></div>}
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.status === 'pending_vetting' && isOfficial && !isMe && !mine && (
                    <>
                      <button onClick={() => { setVetting({ request: r, action: 'approve' }); setNotes(''); }} className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"><Check size={16} className="mr-1" /> Approve</button>
                      <button onClick={() => { setVetting({ request: r, action: 'reject' }); setNotes(''); }} className="flex items-center px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded-md hover:bg-red-100"><X size={16} className="mr-1" /> Reject</button>
                    </>
                  )}
                  {r.status === 'pending_vetting' && isMe && <span className="text-xs text-gray-500">You cannot approve your own request.</span>}
                  {r.status === 'pending_vetting' && mine && <span className="text-xs text-gray-500">You {mine.action === 'approve' ? 'approved' : 'rejected'} this.</span>}
                  {r.status === 'approved' && r.in_needy_list && !r.welfare_plan_id && canCreatePlan && (
                    <button onClick={() => { setPlanFor(r); setPlanBill(''); }} disabled={createPlan.isPending} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100">Start a welfare collection</button>
                  )}
                  {r.welfare_plan_id && <Link to="/welfare" className="px-3 py-1.5 text-sm text-green-700 hover:underline">Collection started</Link>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Modal isOpen={showNew} onClose={() => setShowNew(false)} title={isOfficial ? 'New welfare request' : 'Ask for help'}>
        <form onSubmit={submitNew} className="space-y-3">
          {isOfficial && (
            <label className="block text-sm text-gray-700">Member
              <select required value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="">Choose a member</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
          )}
          {isOfficial && form.member_id && <MemberWelfareRecord memberId={Number(form.member_id)} compact />}
          <label className="block text-sm text-gray-700">What is it for
            <input required maxLength={255} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" placeholder="e.g. Hospital bill" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm text-gray-700">Type
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2">
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block text-sm text-gray-700">Amount (KES)
              <input required type="number" min="0" value={form.amount_requested} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
          </div>
          <label className="block text-sm text-gray-700">More details (optional)
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md">Cancel</button>
            <button type="submit" disabled={create.isPending} className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">{create.isPending ? 'Sending...' : 'Send request'}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!planFor} onClose={() => setPlanFor(null)} title="Start a welfare collection">
        <p className="text-sm text-gray-600 mb-3">{planFor?.member?.name}: {planFor?.title}. Every member is added to the collection.</p>
        <PaybillSelect value={planBill} onChange={setPlanBill} label="Collect into" />
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={() => setPlanFor(null)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md">Cancel</button>
          <button
            onClick={() => createPlan.mutate({ id: planFor.id, mpesa_credential_id: planBill }, { onSuccess: () => setPlanFor(null) })}
            disabled={createPlan.isPending}
            className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
          >{createPlan.isPending ? 'Starting...' : 'Start collection'}</button>
        </div>
      </Modal>

      <Modal isOpen={!!vetting} onClose={() => setVetting(null)} title={vetting?.action === 'approve' ? 'Approve this request' : 'Reject this request'}>
        <p className="text-sm text-gray-600 mb-3">{vetting?.request?.member?.name}: {vetting?.request?.title}</p>
        <label className="block text-sm text-gray-700">Note (optional)
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" />
        </label>
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={() => setVetting(null)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md">Cancel</button>
          <button onClick={submitVet} disabled={vet.isPending} className={`px-4 py-2 text-white rounded-md disabled:opacity-50 ${vetting?.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
            {vetting?.action === 'approve' ? 'Approve' : 'Reject'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
